import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const EmailMarketing = () => {
  const testimonials = [
    {
      id: 1,
      name: "Sneha Rajput",
      review:
        "The Email Marketing Expert course exceeded my expectations! I learned how to build high-converting lists, segment audiences, and write compelling email copy. My open rates and conversions have never been better. Highly recommend this training in Noida!",
      rating: 5,
      date: "2024-06-01",
      profileImg: "https://randomuser.me/api/portraits/women/28.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Harsh Sharma",
      review:
        "From setting up automation flows to A/B testing subject lines, this course covered everything. The practical modules on Mailchimp and HubSpot were incredibly useful. I'm now confidently running email campaigns for my clients.",
      rating: 5,
      date: "2024-05-28",
      profileImg: "https://randomuser.me/api/portraits/men/38.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Priya Malik",
      review:
        "I wanted to understand email marketing beyond just sending newsletters. This course delivered! Lifecycle marketing, GDPR compliance, and advanced analytics were taught clearly. Great instructors and a supportive learning environment.",
      rating: 4,
      date: "2024-05-25",
      profileImg: "https://randomuser.me/api/portraits/women/48.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Ankit Gupta",
      review:
        "Fantastic course for anyone looking to master email marketing. The focus on deliverability and avoiding spam filters was particularly insightful. My business has seen a significant boost in repeat customers thanks to effective email campaigns.",
      rating: 5,
      date: "2024-05-22",
      profileImg: "https://randomuser.me/api/portraits/men/58.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Ritika Devi",
      review:
        "The practical exercises and real-world case studies made learning engaging. I can now segment my audience effectively and create personalized email experiences. The placement team also did a great job connecting me with opportunities.",
      rating: 4,
      date: "2024-05-19",
      profileImg: "https://randomuser.me/api/portraits/women/68.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

   const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const faqContent = [
    {
      title: "What is Email Marketing and why is it essential for businesses?",
      answer:
        "Email Marketing is a highly effective digital marketing channel that involves sending commercial messages to a group of people via email. It's essential because it offers direct communication, high ROI, personalization capabilities, customer retention, and lead nurturing, making it crucial for building customer relationships and driving sales.",
    },
    {
      title: "What are the prerequisites for joining this Email Marketing Expert course?",
      answer:
        "No specific technical prerequisites are required. A basic understanding of digital marketing concepts and familiarity with using email are beneficial. This course is suitable for aspiring digital marketers, content creators, sales professionals, business owners, and anyone interested in building direct customer relationships.",
    },
    {
      title: "What key Email Marketing concepts and platforms will I master?",
      answer:
        "You will master **Email List Building, Segmentation, Email Copywriting, Campaign Creation, Automation & Drip Campaigns, A/B Testing, Deliverability, Analytics & Reporting, and Compliance (GDPR, CAN-SPAM)**. You'll gain hands-on experience with popular **Email Service Providers (ESPs) like Mailchimp, HubSpot, and ConvertKit**.",
    },
    {
      title: "Is there a high demand for Email Marketing professionals?",
      answer:
        "Yes, the demand for skilled Email Marketing professionals is consistently high. Businesses rely on email for lead nurturing, customer retention, and driving sales due to its high ROI. Experts who can strategize, execute, and optimize email campaigns are highly valued.",
    },
    {
      title: "Will I get placement assistance after completing the course?",
      answer:
        "Absolutely! We offer dedicated placement assistance to our Email Marketing graduates. This includes resume development, mock interview preparation, soft skills training, and connecting you with leading digital marketing agencies and companies in Noida and across India for roles like Email Marketing Specialist, CRM Executive, and Digital Marketing Executive.",
    },
    {
      title: "What kind of practical projects will I work on?",
      answer:
        "You will gain hands-on experience by building email lists, designing email templates, writing compelling email copy, setting up automated email sequences (drip campaigns), conducting A/B tests, analyzing campaign performance, and developing a full email marketing strategy for a real or mock business.",
    },
    {
      title: "What are the career opportunities after this Email Marketing training?",
      answer:
        "Upon successful completion, you can pursue exciting careers as an Email Marketing Specialist, CRM Executive, Digital Marketing Executive, Marketing Automation Specialist, Content Marketing Specialist (with email focus), or even a freelance Email Marketing Consultant.",
    },
    {
      title: "What is the course duration and mode of training?",
      answer:
        "The course duration varies based on the pace (intensive fast-track vs. regular batch). We provide flexible training options including immersive offline classroom training at our Noida center and interactive live online classes.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: Introduction to Email Marketing & Strategy",
      description: "Understanding email marketing's role in the marketing funnel, setting goals, and developing a comprehensive email strategy.",
    },
    {
      title: "Module 2: Building & Managing Email Lists",
      description: "Strategies for ethical list building (opt-in forms, lead magnets), list hygiene, and segmenting your audience for personalization.",
    },
    {
      title: "Module 3: Email Service Providers (ESPs) Overview & Setup",
      description: "Exploring popular ESPs like Mailchimp, HubSpot, ConvertKit; setting up accounts, integrating with websites, and managing contacts.",
    },
    {
      title: "Module 4: Email Copywriting & Design Best Practices",
      description: "Crafting compelling subject lines, body copy, call-to-actions (CTAs), mobile-responsive design, and visual elements.",
    },
    {
      title: "Module 5: Campaign Creation & Deployment",
      description: "Setting up various campaign types (newsletters, promotional, transactional), scheduling, and audience targeting within ESPs.",
    },
    {
      title: "Module 6: Email Automation & Drip Campaigns",
      description: "Designing automated workflows for welcome sequences, abandoned carts, lead nurturing, re-engagement, and customer onboarding.",
    },
    {
      title: "Module 7: A/B Testing & Optimization",
      description: "Testing subject lines, email content, CTAs, send times, and audience segments to improve campaign performance.",
    },
    {
      title: "Module 8: Email Deliverability & Compliance",
      description: "Understanding spam filters, sender reputation, authentication (SPF, DKIM), and legal compliance (GDPR, CAN-SPAM).",
    },
    {
      title: "Module 9: Email Marketing Analytics & Reporting",
      description: "Key metrics (open rate, CTR, conversion rate), interpreting reports, Google Analytics integration, and demonstrating ROI.",
    },
    {
      title: "Module 10: Lifecycle Email Marketing",
      description: "Developing strategies for different customer lifecycle stages: acquisition, activation, retention, and win-back campaigns.",
    },
    {
      title: "Module 11: Advanced Email Marketing Tactics",
      description: "Personalization at scale, dynamic content, loyalty programs via email, and integrating email with other marketing channels.",
    },
    {
      title: "Module 12: Live Project & Strategy Development",
      description: "Hands-on development of a complete email marketing strategy, including list building, campaign execution, automation, and reporting for a real-world scenario.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Email Marketing Expert Course in Noida | Automation & CRM Training</title>
        <meta
          name="description"
          content="Become a certified Email Marketing Expert in Noida. Master list building, automation, content, analytics, and deliverability with practical training and 100% placement assistance."
        />
        <meta
          name="keywords"
          content="Email Marketing expert course Noida, Email automation training, CRM email marketing, Mailchimp course, HubSpot email, Lead Nurturing, Digital Marketing, Email List Building, Email Marketing certification"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://multivisionwizards.com/images/web/dm/email.jpg"
          alt="Email Marketing Expert Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              Become an <span className="text-blue-400">Email Marketing Expert</span> in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Master strategies for building lists, sending effective campaigns, and automating communications to drive sales and customer loyalty.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-blue-700 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
                            <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("Email Marketing Training")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn Email Marketing Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Master Email Marketing?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            Email marketing consistently delivers one of the highest returns on investment among all digital channels. It's crucial for direct customer communication, lead nurturing, and retention, making it an indispensable skill for any digital marketer.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-purple-700 mb-3">High ROI Channel</h3>
              <p className="text-gray-600">Generate significant returns for every dollar spent on email.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-purple-700 mb-3">Direct Customer Connection</h3>
              <p className="text-gray-600">Build strong, lasting relationships with your audience.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-purple-700 mb-3">Automation & Scalability</h3>
              <p className="text-gray-600">Automate personalized campaigns to reach thousands efficiently.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '📝', title: 'Compelling Copywriting', desc: 'Craft subject lines and content that drive opens and clicks.' },
              { icon: '⚙️', title: 'Automation & Drip Campaigns', desc: 'Set up sophisticated email sequences for lead nurturing and sales.' },
              { icon: '📧', title: 'ESPs Mastery (Mailchimp, HubSpot)', desc: 'Hands-on experience with leading Email Service Providers.' },
              { icon: '👨‍🏫', title: 'Industry Expert Trainers', desc: 'Learn advanced strategies from seasoned email marketing professionals.' },
              { icon: '🎓', title: 'Certification & Portfolio', desc: 'Earn a recognized certificate and build a strong email campaign portfolio.' },
              { icon: '🤝', title: '100% Placement Assistance', desc: 'Dedicated support for job search, resume building, and interviews.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-purple-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-purple-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Platforms */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Platforms You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our Email Marketing Expert training provides extensive hands-on experience with the leading email service providers and essential tools.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
             
              { name: 'AWeber (concepts)', logo: 'https://profitbooks.net/wp-content/uploads/2024/04/AWeber-Logo.png', desc: 'Email marketing platform' },
              { name: 'Canva', logo: 'https://521324.fs1.hubspotusercontent-na1.net/hubfs/521324/__hs-marketplace__/Canva_Circle_Gradient%20(1).jpeg', desc: 'Email design' },
              { name: 'Google Analytics 4', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg', desc: 'Campaign tracking' },
              { name: 'Grammarly (concepts)', logo: 'https://play-lh.googleusercontent.com/6Xe9DWiMC76daosOC80Im9gqe25Q9P55LxJIGPTLbcdFkMXOur4mk8jTVxoqOaiAvAG4', desc: 'Copywriting assistance' },
              
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2 object-contain" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
                {tool.desc && <p className="text-gray-500 text-xs mt-1">{tool.desc}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-purple-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities in Email Marketing</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A certification as an **Email Marketing Expert** equips you for vital roles focused on direct customer engagement and measurable revenue generation.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '📧', title: 'Email Marketing Specialist', desc: 'Design, execute, and optimize email campaigns and automation workflows.' },
              { icon: '⚙️', title: 'Marketing Automation Specialist', desc: 'Implement and manage automated marketing processes across channels.' },
              { icon: '🎯', title: 'CRM Executive', desc: 'Manage customer relationships through targeted email and communication strategies.' },
              { icon: '✍️', title: 'Content Marketing Specialist (Email Focus)', desc: 'Create compelling written content specifically for email campaigns.' },
              { icon: '📈', title: 'Digital Marketing Executive', desc: 'Oversee various digital channels, with a strong focus on email performance.' },
              { icon: '💡', title: 'Freelance Email Strategist', desc: 'Offer specialized email marketing and automation services to clients.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-purple-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-gray-100 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our Email Marketing Expert training program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., Email Marketing Expert Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name (Optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-purple-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-purple-900">
            What Our Email Marketing Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-purple-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-purple-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-purple-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default EmailMarketing;